/* tslint:disable:max-line-length */
import {Injectable} from '@angular/core';
import {UntilDestroy} from '@ngneat/until-destroy';
import {FabButtonsEnum} from './mm-faults-import-dialog.component';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {catchError, filter, firstValueFrom, map} from 'rxjs';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {FieldMinimal} from '@docma-mm-core/field/field';
import * as ExcelJS from 'exceljs';
import {flattenStructure} from '@docma-mm-core/structure/structure';
import {MmProjectService} from '../../mm-project/mm-project.service';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {DomSanitizer} from '@angular/platform-browser';
import {
  Task,
  TaskFileDownloadService,
  TaskScopeEnum,
  TaskService,
  TaskStatus,
  TaskStatusDataTypeEnum,
  TaskStatusEnum,
  TaskTypeEnum,
  TaskVisibilityEnum
} from '@mydocma/task';
import {TusUploadService} from '@docma-mm-core/tus/tus-upload.service';
import {TusUploadProgress} from '@docma-mm-core/tus/tus-upload-progress';
import {FaultsImportStepperService} from './faults-import-stepper/services/fault-import-stepper.service';
import {AppContainerOptions, AppRightDrawerService} from '@mydocma/app-container';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {HttpClient} from '@angular/common/http';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {MmTranslateService} from '../../mm-translate.service';
import {FaultsImportTasksService} from './faults-import-tasks.service';
import {FieldDataTypeEnum} from '@docma-mm-core/field/field-data-type.enum';

@UntilDestroy()
@Injectable()
export class FaultsImportService extends BasedataService {

  public fieldDict: {[Id: string]: FieldMinimal} = {};
  public permittedFieldDict: {[Id: string]: FieldMinimal} = {};
  public mandatoryFieldDict: {[Id: string]: FieldMinimal} = {};
  public importStarted = false;
  private importTask: Task;

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private translateService: MmTranslateService,
              private projectService: MmProjectService,
              private sanitizer: DomSanitizer,
              private taskDownloadService: TaskFileDownloadService,
              private readonly taskService: TaskService,
              private readonly faultsImportTasksService: FaultsImportTasksService,
              private readonly tusUploadService: TusUploadService,
              private readonly importStepperService: FaultsImportStepperService,
              private readonly appContainerOptions: AppContainerOptions,
              private readonly appRightDrawerService: AppRightDrawerService,
              protected http: HttpClient,
              protected configService: ConfigService,
              private readonly notificationService: NotificationService) {

    super(configService);
  }

  public async initialize() {
    const excludedFields = ['AttributeGroup',
      'CreatedOn', 'Creator',
      'ModifiedOn', 'Modifier',
      'DelayGroup', 'LastActivity',
      'ResponsibleCO_Company', 'ResponsibleCT_Company', 'ResponsibleSC_Company',
      'ProjectSpecific_Name2', 'ProjectSpecific2_Name2'];

    this.fieldDict = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
      .pipe(
        filter(x => x.loadState.status === LoadStatusEnum.Loaded),
        map(m => {
          return m.data.filter(x => x.Id != null && x.Permission !== FieldPermission.None && !excludedFields.some(ex => x.Id.startsWith(ex)) ).map(x => [x.Id.toString(), x]);
        }),
      )));

    this.permittedFieldDict = Object.fromEntries(
      Object.values(this.fieldDict)
        .filter(x => (x.Permission === FieldPermission.Read || x.Permission === FieldPermission.Write) && x.Id.startsWith('Structure') === false)
        .sort((a, b) => (a.Project ?? a.Global).localeCompare((b.Project ?? b.Global)))
        .map(x => [x.Id.toString(), x])
    );

    this.mandatoryFieldDict = Object.fromEntries(
      Object.values(this.fieldDict)
        .filter(x => x.Permission === FieldPermission.Mandatory)
        .sort((a, b) => (a.Project ?? a.Global).localeCompare((b.Project ?? b.Global)))
        .map(x => [x.Id.toString(), x])
    );
  }

  public async downloadTemplate(type: FabButtonsEnum) {
    if (type === FabButtonsEnum.template) {
      return;
    }

    const importFields: {[Id: string]: {Field: FieldMinimal, Mandatory: boolean, Index: number, Format?: string} } = {};
    let index = 0;
    let structureColumnIndex = 0;

    switch (type) {
      case FabButtonsEnum.templateByCode:
        if (this.fieldDict.Structure_Name1)
        {
          importFields.Structure_Name1 =  {Field: this.fieldDict.Structure_Name1, Mandatory: false, Index: ++index};
          structureColumnIndex = 0;
        }
        break;
      case FabButtonsEnum.templateByCodePath:
        if (this.fieldDict.Structure_ShortPath)
        {
          importFields.Structure_ShortPath =  {Field: this.fieldDict.Structure_ShortPath, Mandatory: false, Index: ++index};
          structureColumnIndex = 2;
        }
        break;
      case FabButtonsEnum.templateByDescriptionPath:
        if (this.fieldDict.Structure_LongPath)
        {
          importFields.Structure_LongPath =  {Field: this.fieldDict.Structure_LongPath, Mandatory: false, Index: ++index};
          structureColumnIndex = 3;
        }
        break;
      case FabButtonsEnum.templateByDisplayPath:
        if (this.fieldDict.Structure_DisplayPath)
        {
          importFields.Structure_DisplayPath =  {Field: this.fieldDict.Structure_DisplayPath, Mandatory: false, Index: ++index};
          structureColumnIndex = 4;
        }
        break;
    }

    const dateTimeFormat = this.translateService.instant('MM.fault.import.create.dateTimeFormat');
    const dateFormat = this.translateService.instant('MM.fault.import.create.dateFormat');

    Object.values(this.mandatoryFieldDict).forEach(f => {
      let format: string;
      if (f.DataType === FieldDataTypeEnum.Date || f.Id.startsWith('Date')) {
        format = dateFormat;
      }
      else if (f.DataType === FieldDataTypeEnum.DateTime) {
        format = dateTimeFormat;
      }
      importFields[f.Id] =  {Field: f, Mandatory: true, Index: ++index, Format: format};
    });

    Object.values(this.permittedFieldDict).forEach(f => {
      let format: string;
      if (f.DataType === FieldDataTypeEnum.Date || f.Id.startsWith('Date')) {
        format = dateFormat;
      }
      else if (f.DataType === FieldDataTypeEnum.DateTime) {
        format = dateTimeFormat;
      }
      importFields[f.Id] =  {Field: f, Mandatory: false, Index: ++index, Format: format};
    });

    const attachmentsName = this.translateService.instant('MM.fault.import.create.download.attachments');
    importFields.attachments =  {Field: {Id: 'attachments', Global: attachmentsName}, Mandatory: false, Index: ++index};



    for (let i = 1; i <= 5; i++) {
      const activityDate = this.translateService.instant('MM.fault.import.create.download.activityDate', {id: i});
      const activityStatus = this.translateService.instant('MM.fault.import.create.download.activityStatus', {id: i});
      const activityDescription = this.translateService.instant('MM.fault.import.create.download.activityDescription', {id: i});
      const activityDeadline = this.translateService.instant('MM.fault.import.create.download.activityDeadline', {id: i});
      const activityAttachments = this.translateService.instant('MM.fault.import.create.download.activityAttachments', {id: i});

      importFields[activityDate] =  {Field: {Id: activityDate, Global: activityDate}, Mandatory: false, Index: ++index, Format: dateTimeFormat};
      importFields[activityStatus] =  {Field: {Id: activityStatus, Global: activityStatus}, Mandatory: false, Index: ++index};
      importFields[activityDescription] =  {Field: {Id: activityDescription, Global: activityDescription}, Mandatory: false, Index: ++index};
      importFields[activityDeadline] =  {Field: {Id: activityDeadline, Global: activityDeadline}, Mandatory: false, Index: ++index, Format: dateFormat};
      importFields[activityAttachments] =  {Field: {Id: activityAttachments, Global: activityAttachments}, Mandatory: false, Index: ++index};
    }

    const importFieldsByIndex = Object.fromEntries(Object.values(importFields).map(x => [x.Index, x]));

    const workbook = new ExcelJS.Workbook();
    const worksheetImport = workbook.addWorksheet(this.translateService.instant('MM.fault.import.create.download.sheets.Import'));

    worksheetImport.addRow(Object.values(importFields).map(f => f.Field.Project ?? f.Field.Global));

    worksheetImport.columns.forEach((column, colIndex) => {
      if (importFieldsByIndex[colIndex + 1]?.Format) {
        column.numFmt = importFieldsByIndex[colIndex + 1].Format;
      }
      if (importFieldsByIndex[(colIndex + 1)].Mandatory) {
        this.setColumnCellsBackgroundColor(column, 'FFFFCCCC');
      }
    });

    this.autoSizeColumns(worksheetImport.columns);



    if (this.fieldDict.hasOwnProperty('Craft_Name1')) {
      const craftName1Field = this.fieldDict.Craft_Name1;
      const craftName2Field = this.fieldDict.Craft_Name1.GroupFields.find(x => x.Id === 'Craft_Name2');

      const worksheetCrafts = workbook.addWorksheet(this.translateService.instant('MM.fault.import.create.download.sheets.Crafts'));
      worksheetCrafts.addRow([(craftName1Field?.Project ?? craftName1Field?.Global), (craftName2Field?.Project ?? craftName2Field?.Global)]);   // , 'Label'

      const crafts = await firstValueFrom(this.projectLoaderService.crafts
        .pipe(
          filter(x => x.loadState.status === LoadStatusEnum.Loaded),
          map(m => {
            return m.data.map(x => [x.Name1, x.Name2]   // , `${x.Name1}~~${x.Name2}`
            );
          }),
        ));

      crafts.forEach(craft => {
        worksheetCrafts.addRow(craft);
      });

      this.setColumnCellsBackgroundColor(worksheetCrafts.columns[0], 'FFFFFFCC');
      this.autoSizeColumns(worksheetCrafts.columns);
    }


    if (this.fieldDict.hasOwnProperty('Company_Name1')) {
      const companyName1Field = this.fieldDict.Company_Name1;
      const companyName2Field = this.fieldDict.Company_Name1.GroupFields.find(x => x.Id === 'Company_Name2');

      const worksheetCompanies = workbook.addWorksheet(this.translateService.instant('MM.fault.import.create.download.sheets.Companies'));
      worksheetCompanies.addRow([(companyName1Field?.Project ?? companyName1Field?.Global), (companyName2Field?.Project ?? companyName2Field?.Global)]);

      const companies = await firstValueFrom(this.projectLoaderService.companies
        .pipe(
          filter(x => x.loadState.status === LoadStatusEnum.Loaded),
          map(m => {
            return m.data
              .filter(x => x.ShortName)
              .map(x => [x.ShortName, x.Name2]    // , `${x.Name1}~~${x.Name2 ?? ''}`
              );
          }),
        ));

      companies.forEach(company => {
        worksheetCompanies.addRow(company);
      });

      this.setColumnCellsBackgroundColor(worksheetCompanies.columns[0], 'FFFFFFCC');
      this.autoSizeColumns(worksheetCompanies.columns);
    }


    const currentProject = await firstValueFrom(this.projectService.currentProject);

    const structureName1Field = this.fieldDict.Structure_Name1;
    const structureName2Field = this.fieldDict.Structure_Name2;
    const structureShortPathField = this.fieldDict.Structure_ShortPath;
    const structureLongPathField = this.fieldDict.Structure_LongPath;
    const structureDisplayPathField = this.fieldDict.Structure_DisplayPath;

    const worksheetStructures = workbook.addWorksheet(this.translateService.instant('MM.fault.import.create.download.sheets.Structures'));
    worksheetStructures.addRow([
      (structureName1Field?.Project ?? structureName1Field?.Global),
      (structureName2Field?.Project ?? structureName2Field?.Global),
      (structureShortPathField?.Project ?? structureShortPathField?.Global),
      (structureLongPathField?.Project ?? structureLongPathField?.Global),
      (structureDisplayPathField?.Project ?? structureDisplayPathField?.Global)
    ]);   // , 'Label'

    const rxStructureName1Match = new RegExp(`^${currentProject.Name1}`, 'i');
    const rxStructureName2Match = new RegExp(`^${currentProject.Name2}`, 'i');
    const rxStructureDisplayMatch = new RegExp(`^${currentProject.Name1} - ${currentProject.Name2}`, 'i');

    const structures = await firstValueFrom(this.projectLoaderService.structures
      .pipe(
        filter(x => x.loadState.status === LoadStatusEnum.Loaded),
        map(st => flattenStructure(st.data)),
        map(m => {
          return m
            .map(x => [
                x.Name1,
                x.Name2,
                x.StructurePathShort.replace(rxStructureName1Match, '').replaceAll(' \\ ', '\\'),
                x.StructurePathLong.replace(rxStructureName2Match, '').replaceAll(' \\ ', '\\'),
                x.StructurePath.replace(rxStructureDisplayMatch, '').replaceAll(' \\ ', '\\')
              ]
            );
        }),
      ));

    structures.forEach(structure => {
      worksheetStructures.addRow(structure);
    });

    this.setColumnCellsBackgroundColor(worksheetStructures.columns[structureColumnIndex], 'FFFFFFCC');
    this.autoSizeColumns(worksheetStructures.columns);



    const worksheetStatus = workbook.addWorksheet(this.translateService.instant('MM.fault.import.create.download.sheets.Status'));
    worksheetStatus.addRow([
      this.translateService.instant('MM.fault.import.create.download.statusCode'),
      this.translateService.instant('MM.fault.import.create.download.statusDescription'),
      this.translateService.instant('MM.fault.import.create.download.columnLabel')
    ]);

    const status = await firstValueFrom(this.projectLoaderService.statuses
      .pipe(
        filter(x => x.loadState.status === LoadStatusEnum.Loaded),
        map(m => {
          return m.data
            .filter(x => x.Name1)
            .map(x => [x.Name1, x.Name2, `${x.Name1}~~${x.Name2}~~${(x.ViewPoint === ViewPoint.Customer ? this.translateService.instant('MM.fault.import.create.download.lblCT') : this.translateService.instant('MM.fault.import.create.download.lblSC'))}`]
            );
        }),
      ));

    status.forEach(st => {
      worksheetStatus.addRow(st);
    });

    this.setColumnCellsBackgroundColor(worksheetStatus.columns[2], 'FFFFFFCC');
    this.autoSizeColumns(worksheetStatus.columns);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    await this.taskDownloadService.downloadFile( new File([blob], `Importvorlage_${currentProject.Name1}_${currentProject.Name2}.xlsx`), {open: false, preventBrowserDownload: false});

  }

  private autoSizeColumns(columns: Partial<ExcelJS.Column>[]) {
    columns.forEach((column, colIndex) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = Math.min(50, Math.max(10, maxLength + 2));
    });
  }

  private setColumnCellsBackgroundColor(column: Partial<ExcelJS.Column>, colorValue: string) {
    column.eachCell({ includeEmpty: false }, (cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: colorValue }
      };
    });
  }


  public async import() {

    this.importStarted = true;

    this.importTask = await firstValueFrom(this.taskService.createTask(new Task({
      name: this.translateService.instant('MM.fault.import.task.name', {importFileName: this.importStepperService.selectedFile.ImportFile.name}),
      type: TaskTypeEnum.import,
      visibility: TaskVisibilityEnum.full,
      scope: [TaskScopeEnum.client, TaskScopeEnum.user],
      status: new TaskStatus({ value: TaskStatusEnum.started })
    })));

    this.importTask.status = new TaskStatus({ value: TaskStatusEnum.info });
    this.importTask.status.data = { type: TaskStatusDataTypeEnum.Success, text: this.translateService.instant('MM.fault.import.task.status.uploadingFile') };
    this.taskService.updateTask(this.importTask).subscribe();

    this.faultsImportTasksService.registerTask(this.importTask);

    /*
    * - ON SUCCESS START FAULTS/IMPORT ENDPOINT WITH: FILE-URL & TASK-ID & PROJECT_ID
    *
    * */

    let uploadId: string;

    this.tusUploadService.uploadFile(this.importStepperService.selectedFile.ZipFile ?? this.importStepperService.selectedFile.ImportFile)
      .subscribe({
        next: async (progress: TusUploadProgress) => {
          if (this.importTask.percent < progress.percentage && progress.percentage <= 100) {
            /* RULES FOR SETTING TASK STATUS:
            * - ALWAYS CREATE TASK WITH 'new Task()' + 1ST STATUS AFTER INIT HAS TO BE 'TaskStatusEnum.started'
            * - STATUS FOR SETTING PROGRESS MUST BE 'new TaskStatus({ value: TaskStatusEnum.info, data: { type: TaskStatusDataTypeEnum.Success, text: '<progress message>'} })'
            * - UPDATE PROGRESS ONLY WITH 'new Task({ id: xxx, progress: 0})' */
            this.taskService.updateTask(new Task({ id: this.importTask.id, percent:  Math.round((progress.percentage / 2)) })).subscribe();
          }

          if (progress.finished) {
            uploadId = progress.url.split('/').pop();
          }

          // if (progress.error) {
          //   console.warn('Upload failed!');
          // }
        },
        error: (err) => {
          console.error(err);

          this.importTask.status = new TaskStatus({ value: TaskStatusEnum.failed });
          this.importTask.status.data = {
            type: TaskStatusDataTypeEnum.Error,
            text: this.translateService.instant('MM.fault.import.task.status.upload.error')
          };
          this.taskService.updateTask(this.importTask).subscribe();
        },
        complete: async () => {
          // this.taskService.updateTask(new Task({ id: this.importTask.id, percent: 0 })).subscribe();

          const currentProject = await firstValueFrom(this.projectService.currentProject);
          await this.importFile({
            TaskId: this.importTask.id,
            FileName: (this.importStepperService.selectedFile.ZipFile?.name ?? this.importStepperService.selectedFile.ImportFile.name),
            FileUploadId: uploadId,
            ProjectId: currentProject.Id
          });
        }
      });

    this.appRightDrawerService.openContent('task-dashboard', this.appContainerOptions);
  }

  public async importFile(param: {TaskId: string, FileName: string, FileUploadId: string, ProjectId: number}): Promise<any> {

    const url = super.getFullUrl(`/faults/import`);

    // const formData: FormData = new FormData();
    // formData.append('File', selectedFile.ImportFile, selectedFile.ImportFile.name);
    // formData.append('ProjectId', projectId.toString());
    // selectedFile.Files.forEach(file => {
    //   formData.append('Attachments', file);
    // });

    const response = await this.http.post<any>(url, JSON.stringify(param), { headers: this.getHeaders() })
      .pipe(
        map(data => {
          return data;
        }),
        catchError(e  => this.handleError(e)))
      .toPromise();

    return response;
  }


}
